import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  InvoiceStatus,
  UserRole,
  formatMoney,
  isDistributorRole,
  isSalesRole,
  type Invoice,
  type InvoiceStatus as InvoiceStatusType,
  type UserRole as UserRoleType,
} from '@lombok/shared';
import { Button, Card } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill, statusLabel } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { useSessionProfile } from '@/auth/useSessionProfile';
import { useAccountNames, useAllStatusList, useInvoices } from '@/data/hooks';
import { api } from '@/lib/api';
import { downloadCsv } from '@/lib/csv';
import { INVOICE_STATUS_TABS, INVOICE_TABS, invoiceKindLabel, invoiceNumberLabel } from './labels';
import { invoicesToCsv } from './invoicesCsv';
import { SortSelect } from '@/components/SortSelect';
import { byDate, byNumber, sortItems, thenBy, type SortOption } from '@/lib/sort';

const INVOICE_SORT: SortOption<Invoice>[] = [
  { value: 'recent', label: 'Más recientes', compare: byDate((i) => i.issueDate, 'desc') },
  { value: 'oldest', label: 'Más antiguas', compare: byDate((i) => i.issueDate) },
  { value: 'due', label: 'Vencen primero', compare: byDate((i) => i.dueDate) },
  { value: 'balance_desc', label: 'Saldo mayor', compare: byNumber((i) => i.balanceMinorUsd, 'desc') },
  { value: 'total_desc', label: 'Total mayor', compare: byNumber((i) => i.totalMinorUsd, 'desc') },
  {
    value: 'status',
    label: 'Estado',
    compare: thenBy<Invoice>(
      (a, b) => statusLabel('invoice', a.status).localeCompare(statusLabel('invoice', b.status), 'es'),
      byDate((i) => i.issueDate, 'desc'),
    ),
  },
];

/** Roles whose invoice list is unscoped server-side (finance/admin see all). */
const ALL_ACCESS_ROLES: readonly UserRoleType[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.FINANCE,
];

interface FacturasListPageProps {
  /** Group root for detail links, e.g. "/app/customer/facturas". */
  basePath: string;
  title?: string;
  description?: string;
}

/**
 * Facturas list with status-filter tabs (Todas / Emitidas / Pago parcial / Vencidas /
 * Pagadas / Anuladas). Scoping is server-side: a customer sees their own account (we pass
 * `customerId` from the session profile), a distributor its sub-accounts', finance/admin see all
 * but the backend REQUIRES a `customerId` OR `status` filter — so their "Todas" merges
 * one query per status. Columns: factura, total, saldo, vence, estado. Money is USD via
 * the shared `formatMoney` (minor units). Responsive: table on desktop, cards on mobile.
 */
export function FacturasListPage({
  basePath,
  title = 'Facturas',
  description = 'Facturas y proformas, con su saldo y vencimiento.',
}: FacturasListPageProps) {
  const { profile } = useSessionProfile();
  const isAllAccess = profile ? ALL_ACCESS_ROLES.includes(profile.role) : false;
  // Sales reps see their assigned customers' invoices: the API merges them when no customer is sent.
  const isSales = profile ? isSalesRole(profile.role) : false;
  // A distributor's invoices belong to its sub-accounts: the API merges them when no customer is sent.
  const isDistributor = profile ? isDistributorRole(profile.role) : false;
  const showCustomer = isAllAccess || isSales || isDistributor;

  // Customers pass their own account id; the backend also enforces this. Finance/admin rely on the
  // active status tab; sales and distributors get their customers' invoices merged.
  const customerId = showCustomer ? undefined : profile?.customerId;

  // Opens on "Todas" for every role.
  const [status, setStatus] = useState<InvoiceStatusType | undefined>(undefined);

  // Finance/admin need a filter to query at all, so their "Todas" merges one query per status;
  // account viewers just drop the status. Viewers need their id resolved before the list means anything.
  const ready = showCustomer || Boolean(customerId);
  const mergeAll = isAllAccess && status === undefined;
  const single = useInvoices({ status, customerId, limit: 100 }, ready && !mergeAll);
  const merged = useAllStatusList({
    scope: 'invoices',
    statuses: INVOICE_STATUS_TABS.flatMap((t) => (t.status ? [t.status] : [])),
    fetchStatus: (s) => api.listInvoices({ status: s, limit: 100 }),
    sortDate: (inv) => inv.issueDate,
    enabled: mergeAll,
  });
  const query = mergeAll
    ? { isLoading: merged.isLoading, isError: merged.isError, error: merged.error, refetch: merged.refetch }
    : single;
  const [sort, setSort] = useState('recent');
  const rawItems = mergeAll ? merged.items : (single.data?.items ?? []);
  const items = sortItems(rawItems, INVOICE_SORT, sort);

  // Finance/admin see invoices of every account: show who each one is for (names, not ids).
  const names = useAccountNames(showCustomer);
  const customerName = (id: string) => names.data?.get(id)?.name;
  const exportCsv = () => {
    const tab = INVOICE_TABS.find((t) => t.status === status)?.label ?? 'todas';
    downloadCsv(`facturas-${tab.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`, invoicesToCsv(items, customerName));
  };

  const openCount = useMemo(
    () =>
      status === InvoiceStatus.ISSUED || status === InvoiceStatus.OVERDUE
        ? items.length
        : undefined,
    [status, items.length],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={title}
        description={
          openCount != null
            ? `${description} · ${openCount} en este filtro.`
            : description
        }
        actions={
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!ready || query.isLoading || items.length === 0}>
            Exportar CSV
          </Button>
        }
      />

      {/* Status filter tabs. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="scrollbar-hover -mx-1 flex min-w-0 gap-2 overflow-x-auto px-1 pb-1">
        {INVOICE_TABS.map((tab) => {
          const active = tab.status === status;
          return (
            <button
              key={tab.label}
              type="button"
              onClick={() => setStatus(tab.status)}
              aria-pressed={active}
              className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors ${
                active
                  ? 'border-brand-primary bg-brand-primary/10 font-medium text-brand-primary'
                  : 'border-border text-text-muted hover:border-border-strong hover:text-text-primary'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <SortSelect options={INVOICE_SORT} value={sort} onChange={setSort} />
      </div>

      {!ready ? (
        <Card className="p-6 text-sm text-text-muted">
          Tu usuario aún no está vinculado a una cuenta. No hay facturas que mostrar.
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <QueryState
            isLoading={query.isLoading}
            isError={query.isError}
            error={query.error}
            onRetry={() => void query.refetch()}
            isEmpty={items.length === 0}
            loadingLabel="Cargando facturas…"
            loadingVariant="table"
            skeletonCols={showCustomer ? 6 : 5}
            emptyTitle="No hay facturas"
            emptyDescription="No se encontraron facturas para este filtro."
          >
            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                    <th className="px-4 py-3 font-medium">Factura</th>
                    {showCustomer ? <th className="px-4 py-3 font-medium">Cliente</th> : null}
                    <th className="px-4 py-3 text-right font-medium">Total</th>
                    <th className="px-4 py-3 text-right font-medium">Saldo</th>
                    <th className="px-4 py-3 font-medium">Vence</th>
                    <th className="px-4 py-3 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((inv) => (
                    <InvoiceRow
                      key={inv.invoiceId}
                      invoice={inv}
                      basePath={basePath}
                      customer={showCustomer ? (customerName(inv.customerId) ?? (names.isLoading ? '…' : inv.customerId)) : undefined}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <ul className="divide-y divide-border md:hidden">
              {items.map((inv) => (
                <li key={inv.invoiceId} className="p-4">
                  <Link to={detailTo(basePath, inv)} className="flex flex-col gap-2 active:opacity-80">
                    <div className="flex items-start justify-between gap-3">
                      <span className="flex flex-col">
                        <span className="font-mono text-sm text-brand-primary">{invoiceNumberLabel(inv)}</span>
                        <span className="text-xs text-text-muted">
                          {invoiceKindLabel(inv.kind)}
                          {showCustomer ? ` · ${customerName(inv.customerId) ?? inv.customerId}` : ''}
                        </span>
                      </span>
                      <StatusPill kind="invoice" value={inv.status} />
                    </div>
                    <div className="flex items-end justify-between text-sm">
                      <span className="text-text-muted">Vence {formatDate(inv.dueDate)}</span>
                      <span className="flex flex-col items-end">
                        <span className="text-xs text-text-muted">
                          Total {formatMoney(inv.totalMinorUsd)}
                        </span>
                        <span className="font-semibold text-text-primary">
                          Saldo {formatMoney(inv.balanceMinorUsd)}
                        </span>
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </QueryState>
        </Card>
      )}
    </div>
  );
}

/** A desktop table row linking to the invoice detail (carrying ?customerId=). */
function InvoiceRow({ invoice: inv, basePath, customer }: { invoice: Invoice; basePath: string; customer?: string }) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-surface-muted">
      <td className="px-4 py-3">
        <Link to={detailTo(basePath, inv)} className="flex flex-col">
          <span className="font-mono text-brand-primary hover:underline">{invoiceNumberLabel(inv)}</span>
          <span className="text-xs text-text-muted">{invoiceKindLabel(inv.kind)}</span>
        </Link>
      </td>
      {customer !== undefined ? <td className="px-4 py-3 text-text-muted">{customer}</td> : null}
      <td className="px-4 py-3 text-right text-text-muted">{formatMoney(inv.totalMinorUsd)}</td>
      <td className="px-4 py-3 text-right font-semibold text-text-primary">
        {formatMoney(inv.balanceMinorUsd)}
      </td>
      <td className="px-4 py-3 text-text-muted">{formatDate(inv.dueDate)}</td>
      <td className="px-4 py-3">
        <StatusPill kind="invoice" value={inv.status} />
      </td>
    </tr>
  );
}

/** Detail link that carries the invoice's `customerId` (the partition the detail read needs). */
function detailTo(basePath: string, inv: Invoice) {
  return {
    pathname: `${basePath}/${inv.invoiceId}`,
    search: `?customerId=${encodeURIComponent(inv.customerId)}`,
  };
}

/** Compact local date (es-VE); raw value fallback if unparseable. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
}
